import { useState, useEffect } from 'react'
import {
  useDashboardStats,
  useRecentActivity,
  usePipelineByStage,
  useFollowUp,
  useCallLogStats,
  useRenewalsDue,
  useTasks,
  useUsers,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { format, startOfMonth, endOfMonth, subMonths, addMonths, addDays } from 'date-fns'
import {
  Users,
  Building2,
  Target,
  ClipboardList,
  DollarSign,
  TrendingUp,
  Activity,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Scale,
  Phone,
  Calendar,
} from 'lucide-react'
import {
  formatCurrency,
  formatDateTime,
  cn,
  getGreeting,
  getFullFiscalYearRange,
  getCalendarYearRange,
} from '@/lib/utils'
import {
  CHART_COLORS,
  ROTATED_LABEL_CHART_MARGIN,
  formatChartCurrency,
  GRACKLE_COLORS,
} from '@/lib/theme'
import { BirthdaysWidget } from '@/components/dashboard/BirthdaysWidget'
import { AnniversariesWidget } from '@/components/dashboard/AnniversariesWidget'
import { FollowUpCard } from '@/components/dashboard/FollowUpCard'
import type { FollowUpView } from '@/types'

/** Opportunities list ?activity= key for each follow-up view (dealActivity.js). */
const DEAL_ACTIVITY_FOR_VIEW: Record<FollowUpView, string> = {
  no_next_step: 'no_next_step',
  stale: 'stalled_90',
  stale_no_next: 'stalled_90_no_next',
}
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'

const COLORS = CHART_COLORS

// Date-range presets in display order — single source of truth for both the
// <Select> options and the per-card "active period" label.
const DATE_PRESET_LABELS: Record<string, string> = {
  all: 'All Time',
  fy: 'This Fiscal Year',
  cy: 'This Calendar Year',
  fq: 'This Financial Quarter',
  month: 'This Month',
  last_month: 'Last Month',
  next_month: 'Next Month',
  '30_days': 'Last 30 Days',
  '90_days': 'Last 90 Days',
  next_90_days: 'Next 90 Days',
  custom: 'Custom Date Range',
}

function getDateRange(preset: string): { startDate?: string; endDate?: string } {
  const now = new Date()
  const today = format(now, 'yyyy-MM-dd')

  switch (preset) {
    case 'all':
      return {}
    case 'fy':
      // Full fiscal year, Mar 1 – end of Feb (leap-safe), future-inclusive so it
      // matches the Salesforce fiscal-year reports. Shared with ReportFilters.
      return getFullFiscalYearRange(now)
    case 'cy':
      return getCalendarYearRange(now)
    case 'fq': {
      // Fiscal quarters: Q1 Mar-May, Q2 Jun-Aug, Q3 Sep-Nov, Q4 Dec-Feb
      const m = now.getMonth() // 0-indexed
      let qStart: string, qEnd: string
      if (m >= 2 && m <= 4) {
        qStart = `${now.getFullYear()}-03-01`
        qEnd = `${now.getFullYear()}-05-31`
      } else if (m >= 5 && m <= 7) {
        qStart = `${now.getFullYear()}-06-01`
        qEnd = `${now.getFullYear()}-08-31`
      } else if (m >= 8 && m <= 10) {
        qStart = `${now.getFullYear()}-09-01`
        qEnd = `${now.getFullYear()}-11-30`
      } else {
        // Dec (11), Jan (0), Feb (1)
        const yr = m === 11 ? now.getFullYear() : now.getFullYear() - 1
        qStart = `${yr}-12-01`
        qEnd = `${yr + 1}-02-28`
      }
      return { startDate: qStart, endDate: qEnd }
    }
    case 'month':
      // Full calendar month (not month-to-date). Open opps mostly have future close_dates,
      // so clamping the end to "today" would exclude deals closing later this month and
      // understate every close_date-scoped card. Keep the whole month so all cards reconcile.
      return {
        startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
      }
    case 'last_month': {
      const last = subMonths(now, 1)
      return {
        startDate: format(startOfMonth(last), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(last), 'yyyy-MM-dd'),
      }
    }
    case 'next_month': {
      const next = addMonths(now, 1)
      return {
        startDate: format(startOfMonth(next), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(next), 'yyyy-MM-dd'),
      }
    }
    case '30_days':
      return { startDate: format(subMonths(now, 1), 'yyyy-MM-dd'), endDate: today }
    case '90_days':
      return { startDate: format(addDays(now, -90), 'yyyy-MM-dd'), endDate: today }
    case 'next_90_days':
      return { startDate: today, endDate: format(addDays(now, 90), 'yyyy-MM-dd') }
    default:
      return {}
  }
}

export default function Dashboard() {
  const { currentUser, isAdmin, isSuperAdmin, impersonatedUser } = useUser()
  const [showMyData, setShowMyData] = useState(!isAdmin)
  const [datePreset, setDatePreset] = useState('all')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [selectedRepId, setSelectedRepId] = useState<string>('')

  // Fetch active users for the rep filter (admin-only; non-admins skip the query)
  const { data: usersData } = useUsers(isAdmin ? { limit: 10000 } : undefined)
  const salesReps = isAdmin
    ? (usersData?.data || [])
        .filter((u: any) => u.user_type === 'User' && u.is_active)
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
    : []

  const dateRange =
    datePreset === 'custom'
      ? { ...(customStart && { startDate: customStart }), ...(customEnd && { endDate: customEnd }) }
      : getDateRange(datePreset)

  // Rep dropdown takes priority; falls back to My/All toggle; then all-org view.
  const ownerParam = selectedRepId
    ? { owner_id: selectedRepId }
    : showMyData
      ? { owner_id: impersonatedUser?.id || currentUser?.id }
      : undefined
  const dashboardParams = { ...ownerParam, ...dateRange }

  // Stats (close_date-scoped cards) and the pipeline report now share one date range —
  // getDateRange returns the FULL period for every preset (incl. "This Month"), so there's
  // no today-clamp discrepancy between the Open Pipeline / Weighted / Avg cards anymore.
  const pipelineParams = dashboardParams

  // True for every preset except "All Time"/no-range — used to label which cards the
  // active date range actually scopes (the deal-activity cards, not the current-state totals).
  const hasPeriodFilter = !!(dateRange.startDate || dateRange.endDate)
  // Human label for the active period, reused from the date-range <Select> options below.
  const periodLabel = DATE_PRESET_LABELS[datePreset] ?? ''

  // Build the query string for a stat card so the destination list reflects the
  // SAME dashboard filters that produced the card's number: the active owner scope
  // (specific rep > My Data > all, via ownerParam — also covers impersonation), the
  // active date range, plus the card's own stat type (e.g. status=open|won).
  const cardQuery = (extra: Record<string, string> = {}, opts: { dates?: boolean } = {}) => {
    const { dates = true } = opts
    const p = new URLSearchParams()
    // Carry the exact owner the dashboard is scoped to. Omitted entirely on
    // "All Data" so the destination list shows everyone's records.
    if (ownerParam?.owner_id) p.set('owner_id', ownerParam.owner_id)
    // Period-agnostic cards (e.g. Active Tasks, current-state totals) pass
    // dates:false so the destination list matches the all-records count shown.
    if (dates && dateRange.startDate) p.set('from', dateRange.startDate)
    if (dates && dateRange.endDate) p.set('to', dateRange.endDate)
    Object.entries(extra).forEach(([k, v]) => p.set(k, v))
    const s = p.toString()
    return s ? `?${s}` : ''
  }
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useDashboardStats(dashboardParams)
  const { data: activities, isLoading: activitiesLoading } = useRecentActivity(10, dashboardParams)
  const { data: pipelineData, isLoading: pipelineLoading } = usePipelineByStage(pipelineParams)
  const {
    data: followUp,
    isLoading: followUpLoading,
    isError: followUpError,
  } = useFollowUp(ownerParam)
  const { data: callStats } = useCallLogStats(ownerParam)
  const { data: renewalsDue } = useRenewalsDue(ownerParam)

  // Personal "your day" counts for the welcome header — always the logged-in
  // user's own tasks, regardless of the All/My Data toggle. limit:1 → just the total.
  const welcomeOwner = currentUser?.id ? { owner_id: currentUser.id, limit: 1 } : { limit: 1 }
  const { data: dueTodayTasks } = useTasks({ ...welcomeOwner, date_filter: 'today_open' })
  const { data: overdueTasks } = useTasks({ ...welcomeOwner, date_filter: 'overdue' })

  // Collapsible Renewals Due state
  const [isRenewalsExpanded, setIsRenewalsExpanded] = useState(() => {
    const stored = localStorage.getItem('dashboard-renewals-expanded')
    return stored !== null ? stored === 'true' : false
  })

  useEffect(() => {
    localStorage.setItem('dashboard-renewals-expanded', String(isRenewalsExpanded))
  }, [isRenewalsExpanded])

  // Collapsible Recent Activity state
  const [isActivityExpanded, setIsActivityExpanded] = useState(() => {
    const stored = localStorage.getItem('dashboard-recent-activity-expanded')
    return stored !== null ? stored === 'true' : true
  })

  useEffect(() => {
    localStorage.setItem('dashboard-recent-activity-expanded', String(isActivityExpanded))
  }, [isActivityExpanded])

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    )
  }

  if (statsError) {
    const isRateLimit = (statsError as any)?.response?.status === 429
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle
              className="h-8 w-8 mx-auto mb-3"
              style={{ color: GRACKLE_COLORS.darkRaspberry }}
              aria-hidden="true"
            />
            <h2 className="text-lg font-semibold mb-2">
              {isRateLimit ? 'Rate Limit Reached' : 'Failed to Load Dashboard'}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {isRateLimit
                ? 'Too many requests. Please wait a moment and refresh the page.'
                : 'An error occurred loading dashboard data. Please try again.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-primary underline focus:outline-none focus:ring-2 focus:ring-primary rounded"
            >
              Refresh Page
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // 'Service (Do not Use)' is a forecast-only placeholder (100% probability). It must
  // contribute to Weighted Pipeline but be excluded from every other analytic (counts,
  // value, avg deal size, stage charts) — see backend FORECAST_ONLY_STAGE.
  const SERVICE_STAGE = 'Service (Do not Use)'
  const isClosedStage = (name: string) => name === 'Closed Won' || name === 'Closed Lost'

  // Forecast set: open stages KEEPING Service — used only for Weighted Pipeline.
  const forecastStages = pipelineData?.filter((stage) => !isClosedStage(stage.stage_name)) ?? []
  // Analytics set: open stages EXCLUDING Service — used for charts, counts, value, avg.
  const openStages = forecastStages.filter((stage) => stage.stage_name !== SERVICE_STAGE)

  const opportunityData = openStages.map((stage, index) => ({
    name: stage.stage_name,
    value: Number(stage.opportunity_count),
    color: COLORS[index % COLORS.length],
  }))

  const salesData = openStages.map((stage, index) => ({
    name: stage.stage_name,
    value: Number(stage.total_value),
    color: COLORS[index % COLORS.length],
  }))

  const closedData = pipelineData
    ? pipelineData
        .filter((stage) => stage.stage_name === 'Closed Won' || stage.stage_name === 'Closed Lost')
        .map((stage) => ({
          name: stage.stage_name,
          count: Number(stage.opportunity_count),
          value: Number(stage.total_value),
          color: stage.stage_name === 'Closed Won' ? COLORS[2] : COLORS[3],
        }))
    : []

  // Pipeline summary stats from existing data.
  // Weighted Pipeline includes Won deals (per VP of Sales): every stage EXCEPT
  // Closed Lost — i.e. open stages + Service (100%) + Closed Won (100% → full
  // amount). (forecastStages/openStages above intentionally still exclude Won and
  // drive Open Pipeline / Avg Deal Size / charts.)
  const weightedPipeline = (pipelineData ?? [])
    .filter((s) => s.stage_name !== 'Closed Lost')
    .reduce((sum, s) => sum + Number(s.weighted_value ?? 0), 0)
  const totalOpenCount = openStages.reduce((sum, s) => sum + Number(s.opportunity_count ?? 0), 0)
  const totalOpenValue = openStages.reduce((sum, s) => sum + Number(s.total_value ?? 0), 0)
  const avgDealSize = totalOpenCount > 0 ? totalOpenValue / totalOpenCount : 0

  // Personalized welcome header
  const firstName = currentUser?.name?.trim().split(/\s+/)[0] || 'there'
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const dueTodayCount = dueTodayTasks?.total ?? 0
  const overdueCount = overdueTasks?.total ?? 0
  const renewalCount = renewalsDue?.count ?? 0
  const nearestRenewalDays =
    renewalsDue?.renewals && renewalsDue.renewals.length > 0
      ? Math.min(
          ...renewalsDue.renewals
            .map((r: { days_until_renewal: number }) => Number(r.days_until_renewal))
            .filter((n: number) => Number.isFinite(n))
        )
      : null
  const allCaughtUp = dueTodayCount === 0 && overdueCount === 0 && renewalCount === 0

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-muted-foreground">{todayLabel}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {allCaughtUp ? (
              "You're all caught up for today."
            ) : (
              <>
                {dueTodayCount > 0 && (
                  <Link
                    to="/tasks"
                    className="font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    {dueTodayCount} {dueTodayCount === 1 ? 'task' : 'tasks'} due today
                  </Link>
                )}
                {overdueCount > 0 && (
                  <>
                    {dueTodayCount > 0 && <span aria-hidden="true"> · </span>}
                    <Link
                      to="/tasks"
                      className="font-medium text-destructive underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      {overdueCount} overdue
                    </Link>
                  </>
                )}
                {renewalCount > 0 && (
                  <>
                    {(dueTodayCount > 0 || overdueCount > 0) && <span aria-hidden="true"> · </span>}
                    <span className="font-medium text-foreground">
                      {renewalCount} {renewalCount === 1 ? 'renewal' : 'renewals'} due
                      {nearestRenewalDays != null &&
                        ` (soonest in ${nearestRenewalDays} ${nearestRenewalDays === 1 ? 'day' : 'days'})`}
                    </span>
                  </>
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[200px]" aria-label="Select date range">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DATE_PRESET_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={selectedRepId} onValueChange={setSelectedRepId}>
              <SelectTrigger className="w-[180px]" aria-label="Filter by rep">
                <SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent>
                {salesReps.map((rep: any) => (
                  <SelectItem key={rep.id} value={rep.id}>
                    {rep.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker
                value={customStart}
                onChange={(v) => setCustomStart(v ?? '')}
                className="w-[150px]"
                aria-label="Start date"
              />
              <span className="text-muted-foreground">to</span>
              <DatePicker
                value={customEnd}
                onChange={(v) => setCustomEnd(v ?? '')}
                className="w-[150px]"
                aria-label="End date"
              />
            </div>
          )}
          <Tabs
            value={showMyData ? 'my' : 'all'}
            onValueChange={(value) => setShowMyData(value === 'my')}
          >
            <TabsList>
              <TabsTrigger value="all">All Data</TabsTrigger>
              <TabsTrigger value="my">My Data</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {hasPeriodFilter && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="note">
          <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Cards marked{' '}
            <Calendar className="inline h-3 w-3 align-text-bottom" aria-hidden="true" /> reflect{' '}
            <span className="font-medium text-foreground">{periodLabel}</span>. Total counts always
            show all records.
          </span>
        </p>
      )}

      {/* KPI Cards */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        <Link
          to={`/accounts${cardQuery({}, { dates: false })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View all accounts"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats?.total_accounts ?? 0}</div>
              <p className="text-xs text-muted-foreground leading-tight">
                Active companies in system
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/contacts${cardQuery({}, { dates: false })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View all contacts"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
              <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats?.total_contacts ?? 0}</div>
              <p className="text-xs text-muted-foreground leading-tight">People in your network</p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities${cardQuery()}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View all opportunities"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Opportunities</CardTitle>
              <Target className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {stats?.total_opportunities_all ?? stats?.total_opportunities ?? 0}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">
                {hasPeriodFilter && (
                  <Calendar className="inline h-3 w-3 align-text-bottom mr-1" aria-hidden="true" />
                )}
                {stats?.conversion_rate ?? 0}% conversion rate
                {hasPeriodFilter && ` · ${periodLabel}`}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities${cardQuery({ status: 'open' })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View open pipeline"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Open Pipeline</CardTitle>
              <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">
                {formatCurrency(totalOpenValue, 'CAD')}
              </div>
              <p className="text-xs text-muted-foreground leading-tight">
                {hasPeriodFilter && (
                  <Calendar className="inline h-3 w-3 align-text-bottom mr-1" aria-hidden="true" />
                )}
                Open opportunity value
                {hasPeriodFilter && ` · ${periodLabel}`}
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Link
          to={`/leads${cardQuery({}, { dates: false })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View all leads"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_leads ?? 0}</div>
              <p className="text-xs text-muted-foreground">Leads in pipeline</p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/tasks${cardQuery({ status: 'open' }, { dates: false })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View active tasks"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Tasks</CardTitle>
              <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_tasks ?? 0}</div>
              <p className="text-xs text-muted-foreground">Tasks to complete</p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities${cardQuery({ status: 'won' })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View won revenue"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Won Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(stats?.won_opportunities_value ?? 0, 'CAD')}
              </div>
              <p className="text-xs text-muted-foreground">
                {hasPeriodFilter && (
                  <Calendar className="inline h-3 w-3 align-text-bottom mr-1" aria-hidden="true" />
                )}
                Closed won{hasPeriodFilter ? ` · ${periodLabel}` : ' opportunities'}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities${cardQuery({ status: 'forecast' })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View weighted pipeline"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weighted Pipeline (incl. Won)</CardTitle>
              <Scale className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(weightedPipeline, 'CAD')}</div>
              <p className="text-xs text-muted-foreground">
                {hasPeriodFilter && (
                  <Calendar className="inline h-3 w-3 align-text-bottom mr-1" aria-hidden="true" />
                )}
                Probability-adjusted value
                {hasPeriodFilter && ` · ${periodLabel}`}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities${cardQuery({ status: 'open' })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View opportunities by deal size"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Deal Size</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(avgDealSize, 'CAD')}</div>
              <p className="text-xs text-muted-foreground">
                {hasPeriodFilter && (
                  <Calendar className="inline h-3 w-3 align-text-bottom mr-1" aria-hidden="true" />
                )}
                {totalOpenCount} open opportunities
                {hasPeriodFilter && ` · ${periodLabel}`}
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/tasks${cardQuery({}, { dates: false })}`}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="View call logs"
        >
          <Card className="cursor-pointer transition-all hover:bg-muted h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Calls Today</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{callStats?.today ?? 0}</div>
              <p className="text-xs text-muted-foreground">{callStats?.this_week ?? 0} this week</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Renewals Due Within 90 Days (Collapsible) */}
      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setIsRenewalsExpanded(!isRenewalsExpanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsRenewalsExpanded(!isRenewalsExpanded)
            }
          }}
          aria-expanded={isRenewalsExpanded}
          aria-controls="renewals-due-content"
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle
                className="h-5 w-5"
                style={{ color: GRACKLE_COLORS.darkRaspberry }}
                aria-hidden="true"
              />
              Renewals Due Within 90 Days
              {renewalsDue?.count ? (
                <Badge variant="secondary" className="ml-1">
                  {renewalsDue.count}
                </Badge>
              ) : null}
            </CardTitle>
            <div className="flex items-center gap-2">
              {renewalsDue?.total_value ? (
                <span className="text-sm font-semibold text-foreground">
                  {formatCurrency(renewalsDue.total_value, 'CAD')}
                </span>
              ) : null}
              {isRenewalsExpanded ? (
                <ChevronDown className="h-5 w-5 text-foreground/70" aria-hidden="true" />
              ) : (
                <ChevronUp className="h-5 w-5 text-foreground/70" aria-hidden="true" />
              )}
            </div>
          </div>
          <CardDescription className="text-foreground/80">
            Closed-won opportunities with contract end dates approaching
          </CardDescription>
        </CardHeader>
        <div
          id="renewals-due-content"
          className={cn(
            'overflow-hidden transition-all duration-200',
            isRenewalsExpanded ? 'max-h-screen' : 'max-h-0'
          )}
          role="region"
          aria-hidden={!isRenewalsExpanded}
        >
          <CardContent>
            {renewalsDue?.renewals && renewalsDue.renewals.length > 0 ? (
              <ul className="space-y-3" aria-label="Upcoming renewals">
                {renewalsDue.renewals.slice(0, 10).map((renewal: any) => (
                  <li
                    key={renewal.id}
                    className="flex items-center justify-between border-b pb-2 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/opportunities/${renewal.id}`}
                        className="text-sm font-medium text-primary hover:underline truncate block"
                      >
                        {renewal.name}
                      </Link>
                      <p className="text-xs text-muted-foreground truncate">
                        {renewal.account_name} &middot; {renewal.owner_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="text-sm font-medium">
                        {formatCurrency(renewal.amount_cad ?? renewal.amount ?? 0, 'CAD')}
                      </span>
                      <Badge
                        variant={
                          renewal.days_until_renewal <= 30
                            ? 'destructive'
                            : renewal.days_until_renewal <= 60
                              ? 'warning'
                              : 'secondary'
                        }
                        className="text-xs whitespace-nowrap"
                      >
                        {renewal.days_until_renewal}d
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No renewals due in the next 90 days
              </p>
            )}
          </CardContent>
        </div>
      </Card>

      {/* Follow-up: same live rules as the Exec Sales Dashboard (dealActivity.js) */}
      <div className="grid gap-4 md:grid-cols-3">
        <FollowUpCard
          title="Accounts"
          noun="accounts"
          icon={Building2}
          lists={followUp?.accounts}
          isLoading={followUpLoading}
          isError={followUpError}
          itemHref={(i) => `/accounts/${i.id}`}
          viewAllHref={(v) => `/accounts${cardQuery({ followup: v }, { dates: false })}`}
        />
        <FollowUpCard
          title="Leads"
          noun="leads"
          icon={AlertTriangle}
          lists={followUp?.leads}
          isLoading={followUpLoading}
          isError={followUpError}
          itemHref={(i) => `/leads/${i.id}`}
          viewAllHref={(v) => `/leads${cardQuery({ followup: v }, { dates: false })}`}
        />
        <FollowUpCard
          title="Opportunities"
          noun="opportunities"
          icon={Target}
          lists={followUp?.opportunities}
          isLoading={followUpLoading}
          isError={followUpError}
          itemHref={(i) => `/opportunities/${i.id}`}
          viewAllHref={(v) =>
            `/opportunities${cardQuery({ activity: DEAL_ACTIVITY_FOR_VIEW[v] }, { dates: false })}`
          }
        />
      </div>

      {/* Charts */}
      <div className="grid gap-2 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Sales Pipeline by Stage</CardTitle>
            <CardDescription>Opportunity value by stage</CardDescription>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-muted-foreground">Loading pipeline data...</div>
              </div>
            ) : salesData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No pipeline data available
              </div>
            ) : (
              <figure>
                <figcaption className="sr-only">
                  Sales Pipeline by Stage bar chart. Data:{' '}
                  {salesData.map((s) => `${s.name}: ${formatCurrency(s.value, 'CAD')}`).join(', ')}.
                </figcaption>
                <ResponsiveContainer width="100%" height={300} aria-hidden="true">
                  <BarChart data={salesData} margin={ROTATED_LABEL_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Legend />
                    <Bar dataKey="value" fill={COLORS[0]}>
                      {salesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </figure>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Opportunities by Stage</CardTitle>
            <CardDescription>Distribution of opportunities across pipeline stages</CardDescription>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-muted-foreground">Loading pipeline data...</div>
              </div>
            ) : opportunityData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No pipeline data available
              </div>
            ) : (
              <figure>
                <figcaption className="sr-only">
                  Opportunities by Stage pie chart. Data:{' '}
                  {opportunityData.map((s) => `${s.name}: ${s.value} opportunities`).join(', ')}.
                </figcaption>
                <ResponsiveContainer width="100%" height={300} aria-hidden="true">
                  <PieChart>
                    <Pie
                      data={opportunityData}
                      cx="50%"
                      cy="45%"
                      outerRadius={70}
                      fill={COLORS[0]}
                      dataKey="value"
                    >
                      {opportunityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </figure>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Closed Opportunities</CardTitle>
            <CardDescription>Won and lost opportunity value</CardDescription>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-muted-foreground">Loading closed opportunities data...</div>
              </div>
            ) : closedData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No closed opportunities data available
              </div>
            ) : (
              <figure>
                <figcaption className="sr-only">
                  Closed Opportunities bar chart. Data:{' '}
                  {closedData.map((s) => `${s.name}: ${formatCurrency(s.value, 'CAD')}`).join(', ')}
                  .
                </figcaption>
                <ResponsiveContainer width="100%" height={300} aria-hidden="true">
                  <BarChart data={closedData} margin={ROTATED_LABEL_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Bar dataKey="value" fill={COLORS[0]}>
                      {closedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </figure>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setIsActivityExpanded(!isActivityExpanded)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsActivityExpanded(!isActivityExpanded)
            }
          }}
          aria-expanded={isActivityExpanded}
          aria-controls="recent-activity-content"
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" aria-hidden="true" />
              Recent Activity
            </CardTitle>
            {isActivityExpanded ? (
              <ChevronDown className="h-5 w-5 text-foreground/70" aria-hidden="true" />
            ) : (
              <ChevronUp className="h-5 w-5 text-foreground/70" aria-hidden="true" />
            )}
          </div>
          <CardDescription className="text-foreground/80">
            Latest updates across your CRM
          </CardDescription>
        </CardHeader>
        <div
          id="recent-activity-content"
          className={cn(
            'overflow-hidden transition-all duration-200',
            isActivityExpanded ? 'max-h-screen' : 'max-h-0'
          )}
          role="region"
          aria-hidden={!isActivityExpanded}
        >
          <CardContent>
            {activitiesLoading ? (
              <div className="text-sm text-muted-foreground">Loading activities...</div>
            ) : activities && activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-4 border-b pb-4 last:border-0 last:pb-0"
                  >
                    <div className="flex-1 space-y-1">
                      <p className="text-sm font-medium leading-none">{activity.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDateTime(activity.timestamp)}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">{activity.type}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No recent activity</div>
            )}
          </CardContent>
        </div>
      </Card>

      {/* HR — Birthdays & anniversaries. HR data is Super-Admin-only, so these widgets
          (and their /hr/* calls) only render for Super Admins. */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BirthdaysWidget />
          <AnniversariesWidget />
        </div>
      )}
    </div>
  )
}
